<template>
  <div
    :class="{ 'd-none': hidden, 'd-flex': !hidden }"
    class="flex-column flex-grow-1"
    style="height: calc(100vh - 152px);"
  >
    <div
      id="ai-chat-messages"
      ref="messages"
      class="flex-grow-1 card mb-2"
    >
      <div class="card-body">
        <div v-if="messages.length">
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="['message', `message-${message.role}`]"
          >
            <p v-html="message.content" />
            <img
              v-if="message.imageUrl"
              class="mt-2"
              :src="message.imageUrl"
              style="width: 200px; height: auto"
            >
            <div
              v-if="message.proofLink"
              class="input-group mt-2"
            >
              <input
                :id="`proof-link-${index}`"
                type="text"
                class="form-control"
                :value="message.proofLink"
                readonly
              >
              <button
                class="btn btn-primary"
                @click="copyProofLink(`proof-link-${index}`)"
              >
                <i
                  class="fa fa-clipboard"
                  aria-hidden="true"
                />
                Copy
              </button>
            </div>
            <button
              v-if="message.hasSelectButton"
              class="btn btn-primary mt-2"
              @click="selectProduct(message.productId)"
            >
              Select this product
            </button>
          </div>
        </div>
        <div
          v-else
          class="w-100 h-100 d-flex flex-column justify-content-center align-items-center"
        >
          <p class="h5">
            How can I assist you?
          </p>
          <p class="text-muted">
            I can find elements, edit text, and more.
          </p>
        </div>
      </div>
    </div>
    <div class="mt-2 text-right">
      <a
        href="javascript:void(0);"
        @click="clearChat()"
      >
        New chat
      </a>
    </div>
    <b-input-group class="mt-2">
      <b-textarea
        v-model="userInput"
        placeholder="Type your message..."
        :disabled="loading"
        max-rows="8"
        @keyup.enter="sendMessage"
      />
      <b-button
        v-b-tooltip.hover.viewport.noninteractive="'Send'"
        variant="primary"
        size="sm"
        :disabled="loading"
        class="ml-1"
        @click="sendMessage"
      >
        <i class="cil-send" />
      </b-button>
    </b-input-group>
    <div
      v-if="loading"
      class="position-absolute w-100 h-100 bg-white d-flex flex-column justify-content-center align-items-center z-3"
    >
      <div
        class="spinner-border"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
/* global fontFamilyManager */
import { mapGetters } from "vuex";
import {
    editorControlEventBroker,
    EVENTS,
} from "@frontend/group/modules/editor-control-popup/event-broker";
import {
    HORIZONTAL_GUIDE_LINE_PREFIX_ID,
    VERTICAL_GUIDE_LINE_PREFIX_ID,
} from "@frontend/constants/templates-field-prefixes";
import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from "@frontend/constants/global-update";
import selectedTemplateForApplyToAll from "@frontend/group/modules/editor-control-popup/selected-template-for-apply-to-all";
import aiChatFunctions from "@frontend/components/modules/layouts-page/aiChatFunctions";
import toastr from 'toastr';
import FileUploadService from '@frontend/services/api/file-upload';

export default {
    name: "AiEditorTab",
    props: {
        targetElement: {
            type: fabric.Object,
            require: true,
            default: () => {},
        },
        isTabActive: {
            type: Boolean,
            default: false,
        },
        hidden: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        messages: [],
        userInput: "",
        loading: false,
        layersOrdered: [],
    }),
    computed: {
        ...mapGetters("layout", ["layers", "target", "selectedInstance"]),
    },
    watch: {
        isTabActive() {
            this.rerenderList();
        },
    },
    methods: {
        getFonts() {
            const fonts = [];

            const fontsList = fontFamilyManager.getFontList() || {};

            for (const key in fontsList) {
                const font = fontsList[key];
                const selectedFontFamily = this.value;

                if (Array.isArray(font)) {
                    fonts.push({
                        name: key,
                        value: font.includes(selectedFontFamily)
                            ? selectedFontFamily
                            : font[0],
                    });
                } else {
                    fonts.push({
                        name: font,
                        value: key,
                    });
                }
            }

            return fonts;
        },
        async callOpenAI(settings) {
            const { data } = await axios.post(`/call-open-ai`, settings);
            return data;
        },
        setActiveObject(object) {
            if (!object) {
                return;
            }
            this.selectedInstance.canvas.setActiveObject(object);
            this.selectedInstance.canvas.renderAll();
        },
        reorderLayers(instanceId) {
            const usersFieldsOrder = JSON.parse(
                this.selectedInstance.data.users_fields_order || null,
            );
            this.selectedInstance.canvas.fire("layers:update", instanceId);
            if (usersFieldsOrder) {
                this.layersOrdered = _.sortBy(this.layers, (layer) =>
                    usersFieldsOrder.findIndex((id) => id === layer.id),
                );
                this.moveRulerGuideLinesToFront();
                return;
            }
            this.layersOrdered = this.layers;
            this.moveRulerGuideLinesToFront();
        },
        rerenderList() {
            if (this.isTabActive) {
                this.reorderLayers(this.selectedInstance.id);
            }
        },
        moveRulerGuideLinesToFront() {
            this.selectedInstance.canvas
                .getObjects()
                .filter((obj) =>
                    [
                        HORIZONTAL_GUIDE_LINE_PREFIX_ID,
                        VERTICAL_GUIDE_LINE_PREFIX_ID,
                    ].some((id) => obj.id.startsWith(id)),
                )
                .forEach((obj) => obj.bringToFront());
        },
        async getImageDimensions(blob) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                const url = URL.createObjectURL(blob);

                img.onload = () => {
                    URL.revokeObjectURL(url);
                    resolve({ width: img.width, height: img.height });
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    reject(new Error("Failed to load the image blob."));
                };

                img.src = url;
            });
        },
         clearChat() {
            this.messages = [];
            
            this.userInput = "";
            
            this.$nextTick(() => {
            const messagesDiv = this.$refs.messages;
            if (messagesDiv) {
                messagesDiv.scrollTop = 0;
            }
            });
        },
        async sendMessage() {
            if (!this.userInput.trim() || this.loading) return;

            const userMessage = {
                role: "user",
                content: this.userInput.trim(),
            };

            this.messages.push(userMessage);
            this.userInput = "";
            this.loading = true;

            try {
                let currentSelectedElement = null;
                
                if (this.selectedInstance && this.selectedInstance.canvas) {
                    currentSelectedElement = this.selectedInstance.canvas.getActiveObject();
                }
                
                if (!currentSelectedElement && this.targetElement && Object.keys(this.targetElement).length > 0) {
                    currentSelectedElement = this.targetElement;
                }
                
                // We need to get only the canvas data that is necessary for the AI's context.
                const layersOrderedPrettyJson = JSON.stringify(
                    this.layersOrdered.map(function (layer) {
                        return {
                            element_id: layer.element_id,
                            id: layer.id,
                            name: layer.name,
                            note: layer.note,
                            details: layer.relatedCanvasObject
                                ? {
                                      angle:
                                          layer.relatedCanvasObject.angle ??
                                          null,
                                      charSpacing:
                                          layer.relatedCanvasObject
                                              .charSpacing ?? null,
                                      cornerSize:
                                          layer.relatedCanvasObject
                                              .cornerSize ?? null,
                                      fill:
                                          layer.relatedCanvasObject.fill ??
                                          null,
                                      fontColor:
                                          layer.relatedCanvasObject.fontColor ??
                                          null,
                                      fontFamily:
                                          layer.relatedCanvasObject
                                              .fontFamily ?? null,
                                      fontSize:
                                          layer.relatedCanvasObject.fontSize ??
                                          null,
                                      opacity:
                                          layer.relatedCanvasObject
                                              .teopacityxt ?? null,
                                      text:
                                          layer.relatedCanvasObject.text ??
                                          null,
                                      textAlign:
                                          layer.relatedCanvasObject.textAlign ??
                                          null,
                                  }
                                : null,
                        };
                    }),
                    null,
                    2,
                );
                
                // Get the element_id of the currently selected element
                let selectedLayerElementId = "none";
                if (currentSelectedElement) {
                    selectedLayerElementId = 
                        currentSelectedElement.templateField?.element_id || 
                        currentSelectedElement.element_id || 
                        currentSelectedElement.id || 
                        "none";
                }
                
                const responseMessage = await this.callOpenAI({
                    messages: [
                        {
                            role: "system",
                            content: `You are a banner ad designer AI assistant. Here is the current list of layers in the template: \n\n${layersOrderedPrettyJson}\n\nThe element_id of the currently selected layer is: ${selectedLayerElementId}`,
                        },
                        // Send only the latest 20 messages
                        ...this.messages.slice(-20).map((message) => ({
                            role: message.role,
                            content: message.content,
                        })),
                    ],
                    model: "o3",
                    functions: aiChatFunctions,
                });
                let assistantMessage;

                if (responseMessage.function_call) {
                    const functionName = responseMessage.function_call.name;
                    const functionArgs = JSON.parse(
                        responseMessage.function_call.arguments,
                    );

                    if (functionName === "select_element") {
                        const layer = this.layersOrdered.find(
                            (layer) =>
                                layer.element_id === functionArgs.element_id,
                        );
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an element with ID: "${functionArgs.element_id}". This element may not exist.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        assistantMessage = {
                            role: "assistant",
                            content: `I've selected the element with ID: "${functionArgs.element_id}"`,
                        };
                    } else if (functionName === "change_element_product_id") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an element with ID: "${functionArgs.element_id}". Please select an image element first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        const response = await axios.get(`/file/file_list`, {
                            params: {
                                pageSize: 12,
                                pageNumber: 1,
                                searchKey:
                                    functionArgs.new_product_id.toString(),
                            },
                        });
                        if (response.data.items.length === 0) {
                            assistantMessage = {
                                role: "assistant",
                                content: `The product ID/GTIN/ASIN was not found in our database: ${functionArgs.new_product_id}`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                            action: "productImage",
                            value: response.data.items[0].name.split(".")[0],
                        });
                        assistantMessage = {
                            role: "assistant",
                            content: `I've changed the product ID/GTIN/ASIN of the element with ID: "${functionArgs.element_id}". The new ID is: ${functionArgs.new_product_id}`,
                        };
                    } else if (functionName === "replace_element_image") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an image with ID: "${functionArgs.element_id}". Please select an image first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);

                        const imageUrl = this.target?.getSrc();
                        if (!imageUrl) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't get the source image. Please try selecting the image again.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        const formData = new FormData();
                        let imageWidth = 0;
                        let imageHeight = 0;

                        fetch(imageUrl)
                            .then((response) => response.blob())
                            .then(async (blob) => {
                                const { width, height } =
                                    await this.getImageDimensions(blob);
                                formData.append("width", width);
                                formData.append("height", height);
                                formData.append(
                                    "promptForGenerating",
                                    functionArgs.description_of_the_new_image,
                                );
                                const response = await axios.post(
                                    "/ai-create-image",
                                    formData,
                                    {
                                        headers: {
                                            "Content-Type":
                                                "multipart/form-data",
                                        },
                                    },
                                );

                                const newImageUrl =
                                    window.location.origin + response.data.url;
                                this.messages.push({
                                    role: "assistant",
                                    content: `I've generated a new image to replace the image of the element with ID: "${functionArgs.element_id}". This is the new image.`,
                                    imageUrl: newImageUrl,
                                });
                                this.loading = false;

                                // Wait for the image to show in the chat.
                                setTimeout(() => {
                                    $("#ai-chat-messages").scrollTop(
                                        $("#ai-chat-messages")[0].scrollHeight,
                                    );
                                    setTimeout(() => {
                                        const confirmed = confirm(
                                            "Do you want to save the new image?",
                                        );

                                        if (!confirmed) {
                                            this.messages.push({
                                                role: "assistant",
                                                content: `No changes were applied to the image.`,
                                            });
                                            this.loading = false;
                                            return;
                                        }

                                        fetch(newImageUrl)
                                            .then((response) => response.blob())
                                            .then((blob) => {
                                                const now = new Date();
                                                const timestamp = now
                                                    .toISOString()
                                                    .replace(/[:.]/g, "-");
                                                const filename = `image-${timestamp}.png`;
                                                const file = new File(
                                                    [blob],
                                                    filename,
                                                    { type: blob.type },
                                                );

                                                const dataTransfer =
                                                    new DataTransfer();
                                                dataTransfer.items.add(file);

                                                editorControlEventBroker.fire(
                                                    EVENTS.IMAGE_CHANGED,
                                                    {
                                                        action: "selectBackground",
                                                        value: undefined,
                                                    },
                                                );
                                                const droppedFiles =
                                                    dataTransfer.files;
                                                const $newBackgroundImage = $(
                                                    "#new-background-image",
                                                );
                                                const $selectBkImgModal =
                                                    $("#selectBkImgModal");
                                                const $buttonSelect = $(
                                                    "#selectBkImgModal #submit",
                                                );
                                                const $compressCheckbox =
                                                    $("#compress_check");
                                                const $fileCaption =
                                                    $newBackgroundImage
                                                        .closest(".input-group")
                                                        .find(
                                                            ".file-caption-name",
                                                        );

                                                $newBackgroundImage.fileinput(
                                                    "readFiles",
                                                    droppedFiles,
                                                );
                                                $newBackgroundImage[0].files =
                                                    droppedFiles;

                                                $fileCaption.val(
                                                    `${droppedFiles.length} file selected`,
                                                );
                                                $fileCaption.addClass(
                                                    "is-valid",
                                                );

                                                if (droppedFiles.length) {
                                                    $compressCheckbox.prop(
                                                        "checked",
                                                        true,
                                                    );

                                                    $buttonSelect.hasClass(
                                                        "active",
                                                    )
                                                        ? $buttonSelect.removeClass(
                                                              "active",
                                                          )
                                                        : $buttonSelect.addClass(
                                                              "active",
                                                          );

                                                    $selectBkImgModal
                                                        .find("#upload")
                                                        .show();
                                                    $selectBkImgModal
                                                        .find(
                                                            ".compress_check, .crop_check",
                                                        )
                                                        .show();
                                                } else {
                                                    $selectBkImgModal
                                                        .find("#upload")
                                                        .hide();
                                                    $selectBkImgModal
                                                        .find(
                                                            ".compress_check, .crop_check",
                                                        )
                                                        .hide();
                                                }

                                                localStorage.setItem(
                                                    GLOBAL_UPDATE_LOCAL_STORAGE_KEY,
                                                    false,
                                                );

                                                this.$root.$emit(
                                                    "productImageReplace",
                                                    {
                                                        dataTransfer,
                                                    },
                                                );
                                            });
                                    }, 100);
                                }, 2000);
                            })
                            .catch((error) => {
                                console.error(
                                    "Error fetching or processing the image:",
                                    error,
                                );
                                this.loading = false;
                            });
                    } else if (functionName === "edit_element_image") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an image with ID: "${functionArgs.element_id}". Please select an image first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);

                        const imageUrl = this.target?.getSrc();
                        if (!imageUrl) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't get the source image. Please try selecting the image again.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        const formData = new FormData();
                        const newFileName = "image.png";

                        fetch(imageUrl)
                            .then((response) => response.blob())
                            .then(async (blob) => {
                                const file = new File([blob], newFileName, {
                                    type: blob.type,
                                });
                                const { width, height } =
                                    await this.getImageDimensions(blob);
                                formData.append("width", width);
                                formData.append("height", height);
                                formData.append("image", file);
                                formData.append(
                                    "promptForEditing",
                                    functionArgs.changes_to_the_image,
                                );
                                const response = await axios.post(
                                    "/ai-edit-image",
                                    formData,
                                    {
                                        headers: {
                                            "Content-Type":
                                                "multipart/form-data",
                                        },
                                    },
                                );

                                const newImageUrl =
                                    window.location.origin + response.data.url;
                                this.messages.push({
                                    role: "assistant",
                                    content: `I've edited the image of the element with ID: "${functionArgs.element_id}". This is the edited image.`,
                                    imageUrl: newImageUrl,
                                });
                                this.loading = false;

                                // Wait for the image to show in the chat.
                                setTimeout(() => {
                                    $("#ai-chat-messages").scrollTop(
                                        $("#ai-chat-messages")[0].scrollHeight,
                                    );
                                    setTimeout(() => {
                                        // Create a new image element to check if it's loaded
                                        const img = new Image();
                                        img.onload = () => {
                                            // Add a small delay after image load to ensure it's fully rendered
                                            setTimeout(() => {
                                                const confirmed = confirm(
                                                    "Do you want to save the edited image?",
                                                );

                                                if (!confirmed) {
                                                    this.messages.push({
                                                        role: "assistant",
                                                        content: `No changes were applied to the image.`,
                                                    });
                                                    this.loading = false;
                                                    return;
                                                }

                                                fetch(newImageUrl)
                                                    .then((response) => response.blob())
                                                    .then((blob) => {
                                                        const now = new Date();
                                                        const timestamp = now
                                                            .toISOString()
                                                            .replace(/[:.]/g, "-");
                                                        const filename = `image-${timestamp}.png`;
                                                        const file = new File(
                                                            [blob],
                                                            filename,
                                                            { type: blob.type },
                                                        );

                                                        const dataTransfer =
                                                            new DataTransfer();
                                                        dataTransfer.items.add(file);

                                                        editorControlEventBroker.fire(
                                                            EVENTS.IMAGE_CHANGED,
                                                            {
                                                                action: "selectBackground",
                                                                value: undefined,
                                                            },
                                                        );
                                                        const droppedFiles =
                                                            dataTransfer.files;
                                                        const $newBackgroundImage = $(
                                                            "#new-background-image",
                                                        );
                                                        const $selectBkImgModal =
                                                            $("#selectBkImgModal");
                                                        const $buttonSelect = $(
                                                            "#selectBkImgModal #submit",
                                                        );
                                                        const $compressCheckbox =
                                                            $("#compress_check");
                                                        const $fileCaption =
                                                            $newBackgroundImage
                                                                .closest(".input-group")
                                                                .find(
                                                                    ".file-caption-name",
                                                                );

                                                        $newBackgroundImage.fileinput(
                                                            "readFiles",
                                                            droppedFiles,
                                                        );
                                                        $newBackgroundImage[0].files =
                                                            droppedFiles;

                                                        $fileCaption.val(
                                                            `${droppedFiles.length} file selected`,
                                                        );
                                                        $fileCaption.addClass(
                                                            "is-valid",
                                                        );

                                                        if (droppedFiles.length) {
                                                            $compressCheckbox.prop(
                                                                "checked",
                                                                true,
                                                            );

                                                            $buttonSelect.hasClass(
                                                                "active",
                                                            )
                                                                ? $buttonSelect.removeClass(
                                                                      "active",
                                                                  )
                                                                : $buttonSelect.addClass(
                                                                      "active",
                                                                  );

                                                            $selectBkImgModal
                                                                .find("#upload")
                                                                .show();
                                                            $selectBkImgModal
                                                                .find(
                                                                    ".compress_check, .crop_check",
                                                                )
                                                                .show();
                                                        } else {
                                                            $selectBkImgModal
                                                                .find("#upload")
                                                                .hide();
                                                            $selectBkImgModal
                                                                .find(
                                                                    ".compress_check, .crop_check",
                                                                )
                                                                .hide();
                                                        }

                                                        localStorage.setItem(
                                                            GLOBAL_UPDATE_LOCAL_STORAGE_KEY,
                                                            false,
                                                        );

                                                        this.$root.$emit(
                                                            "productImageReplace",
                                                            {
                                                                dataTransfer,
                                                            },
                                                        );
                                                    });
                                            }, 100); 
                                        };
                                        img.onerror = () => {
                                            this.messages.push({
                                                role: "assistant",
                                                content: `Sorry, there was an error loading the edited image. Please try again.`,
                                            });
                                            this.loading = false;
                                        };
                                        img.src = newImageUrl;
                                    }, 100);
                                }, 2000);
                            })
                            .catch((error) => {
                                console.error(
                                    "Error fetching or processing the image:",
                                    error,
                                );
                                this.loading = false;
                            });
                    } else if (functionName === "edit_element_text") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a text element with ID: "${functionArgs.element_id}". Please select a text element first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: functionArgs.new_text,
                            action: "inputText",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "text",
                                keys: ["text"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've edited the text of the element with ID: "${functionArgs.element_id}". The new text is: "${functionArgs.new_text}"`,
                        };
                    } else if (functionName === "edit_element_color") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an element with ID: "${functionArgs.element_id}". Please select an element first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        // Check if the element is not an image
                        const isImage = layer.relatedCanvasObject.type === 'image' || 
                                       (layer.relatedCanvasObject.id && 
                                        layer.relatedCanvasObject.id.includes('image_')) ||
                                       (layer.relatedCanvasObject.element_id && 
                                        layer.relatedCanvasObject.element_id.includes('image_'));
                        
                        if (isImage) {
                            assistantMessage = {
                                role: "assistant",
                                content: `The selected field is an image and I can't update the text color for it.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: functionArgs.new_color,
                            action: "fontColor",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "fontColor",
                                keys: ["color"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've edited the text of the element with ID: "${functionArgs.element_id}". The new color is: "${functionArgs.new_color}"`,
                        };
                    } else if (functionName === "edit_element_font_size") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a text element with ID: "${functionArgs.element_id}". Please select a text element first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: functionArgs.new_font_size,
                            action: "fontSize",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "fontSize",
                                keys: ["size"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've edited the text of the element with ID: "${functionArgs.element_id}". The new font size is: "${functionArgs.new_font_size}"`,
                        };
                    } else if (functionName === "edit_element_font_family") {
                        // Use another prompt for selecting the prompt. We do not send the font list in the system prompt to improve accuracy.
                        const responseMessage = await this.callOpenAI({
                            messages: [
                                {
                                    role: "system",
                                    content: `You are a banner ad designer AI assistant.`,
                                },
                                // Send only the latest 20 messages
                                ...this.messages.slice(-20).map((message) => ({
                                    role: message.role,
                                    content: message.content,
                                })),
                                {
                                    role: "system",
                                    content: `You must return a font that satisfies the requeriments of the latest message. The font must be one from the following list:\n\nJSON.parse(JSON.stringify(this.getFonts())).`,
                                },
                            ],
                            response_format: {
                                name: "font",
                                schema: {
                                    type: "object",
                                    properties: {
                                        font: {
                                            anyOf: [
                                                {
                                                    type: "string",
                                                    description:
                                                        "The name of the font.",
                                                },
                                                {
                                                    type: "null",
                                                    description:
                                                        "Null if no font is specified.",
                                                },
                                            ],
                                        },
                                    },
                                    required: ["font"],
                                    additionalProperties: false,
                                },
                                strict: true,
                            },
                            model: "gpt-4o",
                        });
                        const newFontName = JSON.parse(
                            responseMessage.content,
                        ).font;
                        
                        if (!newFontName) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a font that matches your requirements.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a text element with ID: "${functionArgs.element_id}". Please select a text element first.`,
                            };
                            this.messages.push(assistantMessage);
                            this.loading = false;
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: newFontName,
                            action: "fontFamily",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "fontFamily",
                                keys: ["font"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've changed the font of the element with ID: "${functionArgs.element_id}". The new font is: "${newFontName}"`,
                        };
                    } else if (functionName === "edit_project_name") {
                        const instanceId = this.selectedInstance.id;
                        const preview = window.previews.find((preview) => {
                            return preview.id === instanceId;
                        });
                        const grid = $("#template-group-preview");
                        const customer_id = grid.attr("data-customer-id");
                        const layout_id = grid.attr("data-layout-id");
                        const dataForUpdate = [
                            {
                                instance_id: instanceId,
                                key: "project_name",
                                value: functionArgs.new_project_name,
                            },
                        ];
                        try {
                            await axios.put(
                                `/banner/${customer_id}/groups/${layout_id}/save_changes`,
                                dataForUpdate,
                            );
                            preview.data.project_name =
                                functionArgs.new_project_name;
                            const templateTitleElement = $(
                                `.template-item[data-instance=${instanceId}]`,
                            ).find(".template-name-canvas");
                            templateTitleElement.html(
                                window.getTemplateNameText(
                                    preview.template,
                                    preview.data,
                                ),
                            );
                            templateTitleElement.css({
                                fontSize: "18px",
                                fontFamily: "Arial",
                                color: "rgb(0, 0, 0)",
                                fontWeight: "normal",
                            });
                        } catch (e) {
                            console.error(e);
                            assistantMessage = {
                                role: "assistant",
                                content: `There was an error and I couldn't update the project name.`,
                            };
                        } finally {
                            assistantMessage = {
                                role: "assistant",
                                content: `I've successfully updated the project name. The new name is: ${functionArgs.new_project_name}`,
                            };
                        }
                    } else if (functionName === "publish_to_platform") {
                        try {
                            this.loading = true;

                            const platformType =
                                functionArgs.platform_type?.toLowerCase();

                            if (!platformType) {
                                assistantMessage = {
                                    role: "assistant",
                                    content: `I couldn't determine which platform you want to publish to. Please specify Bynder, Salsify, or Walmart.`,
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            $("#publishToPlatformModal").modal("show");

                            setTimeout(() => {
                                const platformSelect = $(
                                    "#deployment_platform",
                                );
                                const options = platformSelect.find("option");
                                let platformFound = false;
                                let multiplePlatforms = false;
                                let matchingPlatformIds = [];

                                options.each(function () {
                                    const optionPlatform = $(this)
                                        .data("platform")
                                        ?.toLowerCase();
                                    if (optionPlatform === platformType) {
                                        matchingPlatformIds.push($(this).val());
                                    }
                                });

                                if (matchingPlatformIds.length > 0) {
                                    platformFound = true;

                                    if (matchingPlatformIds.length > 1) {
                                        multiplePlatforms = true;
                                    } else {
                                        platformSelect
                                            .val(matchingPlatformIds[0])
                                            .trigger("change");
                                    }
                                }

                                if (platformFound) {
                                    if (multiplePlatforms) {
                                        assistantMessage = {
                                            role: "assistant",
                                            content: `I found multiple ${platformType} connections. Please select the specific one you want to use from the publish dialog.`,
                                        };
                                    } else {
                                        assistantMessage = {
                                            role: "assistant",
                                            content: `I've opened the publish dialog with ${platformType} selected for you.`,
                                        };
                                    }
                                } else {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `I couldn't find a ${platformType} connection. Please select a platform from the publish dialog manually.`,
                                    };
                                }

                                this.messages.push(assistantMessage);
                                this.loading = false;
                            }, 500);

                            return;
                        } catch (e) {
                            console.error(
                                "Error setting up publish dialog:",
                                e,
                            );
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error opening the publish dialog. Please try using the Publish button manually.`,
                            };
                            this.loading = false;
                        }
                    } else if (functionName === "generate_proof_link") {
                        try {
                            const layoutVersionId = $(
                                "#template-group-preview",
                            ).attr("data-layout-id");

                            this.loading = true;

                            const response = await axios({
                                method: "get",
                                url: `/banner/groups/${layoutVersionId}/get_latest_share_proof_link_for_version`,
                            });

                            if (response.data && response.data.url) {
                                assistantMessage = {
                                    role: "assistant",
                                    content: `Here's the proof link:`,
                                    proofLink: response.data.url,
                                };
                            } else {
                                const response = await axios.post(
                                    `/banner/groups/${layoutVersionId}/share_proof_link`,
                                    {
                                        from: 0,
                                        template_images: window.previews.map(
                                            (preview) => ({
                                                instance_id: preview.id,
                                                name:
                                                    preview.template?.name ||
                                                    "",
                                            }),
                                        ),
                                    },
                                );

                                if (response.data && response.data.url) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Here's the proof link:`,
                                        proofLink: response.data.url,
                                    };
                                } else {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Sorry, I couldn't generate a proof link. Please try again or use the 'Share' button manually.`,
                                    };
                                }
                            }

                            this.loading = false;
                        } catch (e) {
                            console.error("Error generating proof link:", e);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error generating the proof link. Please try using the 'Share' button manually.`,
                            };
                            this.loading = false;
                        }
                    } else if (functionName === "check_product_id") {
                        try {
                            this.loading = true;

                            // Manually execute the parsing logic that would normally run server-side
                            const rawInput = functionArgs.product_id;
                            let parsedArgs = {};

                            // Check if it contains multiple IDs (contains spaces, commas, or newlines)
                            if (/[\s,\n]/.test(rawInput)) {
                                // Parse multiple identifiers
                                const identifiers = rawInput
                                    .split(/[\s,\n]+/) // Split by spaces, commas, or newlines
                                    .map((id) => id.trim())
                                    .filter((id) => id.length > 0); // Remove empty strings

                                if (identifiers.length === 0) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `No valid product identifiers provided.`,
                                    };
                                    this.messages.push(assistantMessage);
                                    this.loading = false;
                                    return;
                                }

                                parsedArgs = { multiple: true, identifiers };
                            } else {
                                // Single identifier case
                                parsedArgs = {
                                    multiple: false,
                                    product_id: rawInput.trim(),
                                };
                            }

                            // Now use the parsed arguments
                            if (parsedArgs.multiple) {
                                // Handle multiple product IDs
                                const { identifiers } = parsedArgs;

                                if (!identifiers || !identifiers.length) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `I need valid product identifiers to check. Please provide ASINs, GTINs, or UPCs.`,
                                    };
                                    this.messages.push(assistantMessage);
                                    this.loading = false;
                                    return;
                                }

                                const results = [];
                                let foundCount = 0;
                                let notFoundCount = 0;

                                // Process each identifier sequentially
                                for (const identifier of identifiers) {
                                    try {
                                        const response = await axios.get(
                                            "/file/file_list",
                                            {
                                                params: {
                                                    pageSize: 1, // Only need to know if it exists
                                                    pageNumber: 1,
                                                    searchKey: identifier,
                                                    exactMatch: true,
                                                },
                                            },
                                        );

                                        const found =
                                            response.data &&
                                            response.data.items &&
                                            response.data.items.length > 0;

                                        results.push({
                                            identifier,
                                            found,
                                            count: found
                                                ? response.data.items.length
                                                : 0,
                                        });

                                        if (found) {
                                            foundCount++;
                                        } else {
                                            notFoundCount++;
                                        }
                                    } catch (error) {
                                        console.error(
                                            `Error checking identifier ${identifier}:`,
                                            error,
                                        );
                                        results.push({
                                            identifier,
                                            found: false,
                                            error: true,
                                        });
                                        notFoundCount++;
                                    }
                                }

                                // Generate a comprehensive response with HTML instead of markdown
                                let content = `<p><strong>Product Identifier Check Results</strong></p>`;
                                content += `<p>Checked ${identifiers.length} identifiers:</p>`;
                                content += `<ul>`;
                                content += `<li>\u2705 Found: ${foundCount}</li>`;
                                content += `<li>\u274c Not found: ${notFoundCount}</li>`;
                                content += `</ul>`;
                                content += `<p><strong>Details:</strong></p>`;
                                content += `<ul>`;

                                // Add each identifier to the content
                                results.forEach((result) => {
                                    if (result.found) {
                                        content += `<li><strong>${result.identifier}</strong>: \u2705 Found</li>`;
                                    } else if (result.error) {
                                        content += `<li><strong>${result.identifier}</strong>: \u2753 Error checking</li>`;
                                    } else {
                                        content += `<li><strong>${result.identifier}</strong>: \u274c Not found</li>`;
                                    }
                                });

                                content += `</ul>`;

                                // Collect all found product IDs for the Select button
                                const foundProductIds = results
                                    .filter((result) => result.found)
                                    .map((result) => result.identifier);

                                const hasFoundProducts =
                                    foundProductIds.length > 0;
                                const allFoundProductIds =
                                    foundProductIds.join(" ");

                                assistantMessage = {
                                    role: "assistant",
                                    content,
                                    // Add hasSelectButton and productId for all found products
                                    hasSelectButton: hasFoundProducts,
                                    productId: allFoundProductIds,
                                };
                            } else {
                                // Handle single product ID (original behavior)
                                const productId = parsedArgs.product_id;

                                if (!productId) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `I need a valid product ID to check. Please provide a product ID, GTIN, or ASIN.`,
                                    };
                                    this.messages.push(assistantMessage);
                                    this.loading = false;
                                    return;
                                }

                                try {
                                    const response = await axios.get(
                                        "/file/file_list",
                                        {
                                            params: {
                                                pageSize: 12,
                                                pageNumber: 1,
                                                searchKey: productId,
                                                exactMatch: true,
                                            },
                                        },
                                    );

                                    if (
                                        response.data &&
                                        response.data.items &&
                                        response.data.items.length > 0
                                    ) {
                                        const foundProducts =
                                            response.data.items;

                                        assistantMessage = {
                                            role: "assistant",
                                            content: `\u2705 Product ID "${productId}" exists in the database. Found ${foundProducts.length} matching item${foundProducts.length > 1 ? "s" : ""}.`,
                                            foundProductData: response.data,
                                            hasSelectButton: true,
                                            productId: productId,
                                        };
                                        this.loading = false;
                                    } else {
                                        assistantMessage = {
                                            role: "assistant",
                                            content: `Product ID "${productId}" was not found in the database. Please check the ID and try again.`,
                                        };
                                        this.loading = false;
                                    }
                                } catch (error) {
                                    console.error(
                                        "Error checking product existence:",
                                        error,
                                    );
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Sorry, there was an error checking if product ID "${productId}" exists. Please try again.`,
                                    };
                                    this.loading = false;
                                }
                            }
                        } catch (e) {
                            console.error("Error in check_product_id:", e);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error processing your request. Please try again.`,
                            };
                            this.loading = false;
                        }
                    } else if (functionName === 'rotate_image' || functionName === 'remove_image_background') {
                        try {
                            // Common image handling logic
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.id === functionArgs.element_id ||
                                            layer.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected image';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an image to modify. Please select an image element first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }
                            
                            // Check if this is actually an image
                            const isImage = layer.relatedCanvasObject.type === 'image' || 
                                (layer.relatedCanvasObject.id && 
                                 layer.relatedCanvasObject.id.includes('image'));
                                 
                            if (!isImage) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element is not an image. Please select an image element.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Function-specific operations
                            if (functionName === 'rotate_image') {
                                const angle = functionArgs.angle || 90; // Default to 90 degrees if not specified

                                // Get current angle
                                const currentAngle = layer.relatedCanvasObject.angle || 0;

                                // Set new angle (adds to the current angle)
                                layer.relatedCanvasObject.rotate(currentAngle + angle);

                                // Update coordinates
                                layer.relatedCanvasObject.setCoords();

                                // Fire the object:rotating event to ensure changes are saved
                                layer.relatedCanvasObject.canvas.fire('object:rotating', { target: layer.relatedCanvasObject });

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've rotated the image by ${angle} degrees.`
                                };
                            } else if (functionName === 'remove_image_background') {
                                try {
                                    // Determine if it's a product image or a theme image
                                    const isProductImage = layer.relatedCanvasObject.id &&
                                        (layer.relatedCanvasObject.id.includes('product_image') ||
                                            layer.relatedCanvasObject.id.includes('image_'));

                                    // Show loading notification
                                    toastr.info(
                                        `<div class="d-inline-flex align-content-center">Removing background...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
                                        null,
                                        {
                                            timeOut: 0,
                                            tapToDismiss: false,
                                            closeButton: false
                                        });

                                    // Get the image file path from the source URL
                                    const imageSrc = layer.relatedCanvasObject.getSrc();
                                    const filePath = new URL(imageSrc).searchParams.get('file');

                                    if (!filePath) {
                                        throw new Error('Failed to extract file path from image source');
                                    }

                                    // Call the background removal API with the correct service
                                    const { data } = await FileUploadService.removeImageBackground({
                                        filePath: filePath,
                                        is_crop_to_image: !!functionArgs.crop_to_image
                                    });

                                    if (!data || !data.imgUrl) {
                                        throw new Error('Invalid response from background removal service');
                                    }

                                    if (isProductImage) {
                                        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                            action: 'removeBackgroundProductImage',
                                            value: {
                                                imgUrl: data.imgUrl,
                                                filePath: filePath,
                                                target: layer.relatedCanvasObject,
                                                isCopyAndReplace: true,
                                                instanceId: this.selectedInstance.id,
                                                isCropToImage: !!functionArgs.crop_to_image
                                            }
                                        });
                                    } else {
                                        // It's a theme image
                                        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                            action: 'removeBackgroundThemeImgBG',
                                            value: {
                                                imgUrl: `/share?file=${data.imgUrl}`,
                                                objectId: layer.relatedCanvasObject.id,
                                                instanceId: this.selectedInstance.id,
                                                target: layer.relatedCanvasObject
                                            }
                                        });
                                    }

                                    toastr.clear();
                                    assistantMessage = {
                                        role: 'assistant',
                                        content: `I've successfully removed the background from the image.`
                                    };
                                } catch (error) {
                                    console.error('Error removing image background:', error);
                                    toastr.clear();
                                    toastr.error('Something went wrong while removing background');
                                    assistantMessage = {
                                        role: 'assistant',
                                        content: `Sorry, there was an error removing the image background. Please try again.`
                                    };
                                }

                                // Always set loading to false and add the message
                                this.loading = false;
                                this.messages.push(assistantMessage);
                                return;
                            }

                            // Common updates for move and rotate operations
                            layer.relatedCanvasObject.canvas.renderAll();

                            // Fire object:moving for all operations to ensure changes are saved
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                        } catch (error) {
                            console.error(`Error in ${functionName}:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error modifying the image. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'center_object') {
                        try {
                            // Use provided element_id or fallback to currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected object';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an object to center. Please select an object first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);
                            
                            // Determine what element type this is to use the appropriate event
                            const elementType = this.getElementType(layer.relatedCanvasObject);
                            const eventType = this.getElementEventType(layer.relatedCanvasObject);
                            
                            // Map alignment to the proper action
                            const alignmentActions = {
                                'both': 'center', // Center both horizontally and vertically
                                'horizontal': 'centerToTemplateWidth', // Center only horizontally
                                'vertical': 'centerToTemplateHeight' // Center only vertically
                            };
                            
                            // Get the action based on the alignment parameter or default to center both
                            const action = alignmentActions[functionArgs.alignment || 'both'] || 'center';
                            
                            // Different actions based on element type
                            if (elementType === 'text') {
                                if (action === 'center') {
                                    // For text, we need to center both horizontally and vertically
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        action: 'centerToTemplateWidth'
                                    });
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        action: 'centerToTemplateHeight'
                                    });
                                } else {
                                    // For horizontal or vertical centering
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        action: action
                                    });
                                }
                            } else if (elementType === 'image') {
                                // Check if this is a smart object by looking for a groupName property
                                const isSmartObject = !!layer.relatedCanvasObject.groupName;
                                
                                if (action === 'center') {
                                    // Create center action data for both horizontal and vertical centering
                                    const centerActionData = {
                                        action: 'centerImage',
                                        tooltip: isSmartObject ? 'Center to SmartObject size' : 'Center to template size',
                                        centerPoint: layer.relatedCanvasObject.canvas.getCenter(),
                                        isSmartObject: isSmartObject
                                    };
                                    
                                    // If it's a smart object, get the proper center point
                                    if (isSmartObject) {
                                        // Find the mask object for this smart object group
                                        const maskObject = layer.relatedCanvasObject.canvas
                                            .getObjects()
                                            .find(obj => obj.groupName === layer.relatedCanvasObject.groupName && 
                                                  (obj.id && obj.id.startsWith('mask_')));
                                                  
                                        if (maskObject) {
                                            centerActionData.centerPoint = maskObject.getCenterPoint();
                                        }
                                    }
                                    
                                    // For images, centerImage centers both horizontally and vertically
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        action: 'centerImage',
                                        value: centerActionData
                                    });
                                } else if (action === 'centerToTemplateWidth') {
                                    // For horizontal centering of images
                                    const horizontalCenterData = {
                                        action: 'horizontalCenter',
                                        tooltip: 'Center Horizontally',
                                        centerPoint: layer.relatedCanvasObject.canvas.getCenter(),
                                        isSmartObject: isSmartObject
                                    };
                                    
                                    if (isSmartObject) {
                                        const maskObject = layer.relatedCanvasObject.canvas
                                            .getObjects()
                                            .find(obj => obj.groupName === layer.relatedCanvasObject.groupName && 
                                                  (obj.id && obj.id.startsWith('mask_')));
                                                  
                                        if (maskObject) {
                                            horizontalCenterData.centerPoint = maskObject.getCenterPoint();
                                        }
                                    }
                                    
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        action: 'horizontalCenter',
                                        value: horizontalCenterData
                                    });
                                } else if (action === 'centerToTemplateHeight') {
                                    // For vertical centering, use the verticalCenter action
                                    const verticalCenterData = {
                                        action: 'verticalCenter',
                                        tooltip: 'Center Vertically',
                                        centerPoint: layer.relatedCanvasObject.canvas.getCenter(),
                                        isSmartObject: isSmartObject
                                    };
                                    
                                    if (isSmartObject) {
                                        const maskObject = layer.relatedCanvasObject.canvas
                                            .getObjects()
                                            .find(obj => obj.groupName === layer.relatedCanvasObject.groupName && 
                                                  (obj.id && obj.id.startsWith('mask_')));
                                                  
                                        if (maskObject) {
                                            verticalCenterData.centerPoint = maskObject.getCenterPoint();
                                        }
                                    }
                                    
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        action: 'verticalCenter',
                                        value: verticalCenterData
                                    });
                                }
                            } else {
                                // For shapes or other objects
                                if (action === 'center') {
                                    // Center both horizontally and vertically
                                    // For horizontal centering of shapes
                                    const canvas = layer.relatedCanvasObject.canvas;
                                    const obj = layer.relatedCanvasObject;
                                    
                                    // First center horizontally
                                    const zoom = canvas.getZoom();
                                    const objectWidth = obj.width * obj.scaleX;
                                    const templateWidth = canvas.width / zoom;
                                    const newLeft = (templateWidth - objectWidth) / 2;
                                    
                                    obj.left = newLeft;
                                    obj.setCoords();
                                    
                                    // Then center vertically
                                    const objectHeight = obj.height * obj.scaleY;
                                    const templateHeight = canvas.height / zoom;
                                    const newTop = (templateHeight - objectHeight) / 2;
                                    
                                    obj.top = newTop;
                                    obj.setCoords();
                                    
                                    // Render and trigger moving event to save changes
                                    canvas.renderAll();
                                    canvas.fire('object:moving', { target: obj });
                                    
                                } else if (action === 'centerToTemplateWidth') {
                                    // For horizontal centering of shapes
                                    const canvas = layer.relatedCanvasObject.canvas;
                                    const obj = layer.relatedCanvasObject;
                                    
                                    const zoom = canvas.getZoom();
                                    const objectWidth = obj.width * obj.scaleX;
                                    const templateWidth = canvas.width / zoom;
                                    const newLeft = (templateWidth - objectWidth) / 2;
                                    
                                    obj.left = newLeft;
                                    obj.setCoords();
                                    canvas.renderAll();
                                    canvas.fire('object:moving', { target: obj });
                                    
                                } else if (action === 'centerToTemplateHeight') {
                                    // For vertical centering of shapes
                                    const canvas = layer.relatedCanvasObject.canvas;
                                    const obj = layer.relatedCanvasObject;
                                    
                                    const zoom = canvas.getZoom();
                                    const objectHeight = obj.height * obj.scaleY;
                                    const templateHeight = canvas.height / zoom;
                                    const newTop = (templateHeight - objectHeight) / 2;
                                    
                                    obj.top = newTop;
                                    obj.setCoords();
                                    canvas.renderAll();
                                    canvas.fire('object:moving', { target: obj });
                                }
                            }
                            
                            // Apply to all templates if requested
                            if (functionArgs.apply_to_all) {
                                editorControlEventBroker.fire(eventType, {
                                    value: true,
                                    action: 'position',
                                    keys: ['position']
                                });
                            }
                            
                            // Create appropriate response message
                            let alignmentDescription = '';
                            switch (functionArgs.alignment) {
                                case 'horizontal':
                                    alignmentDescription = 'horizontally';
                                    break;
                                case 'vertical':
                                    alignmentDescription = 'vertically';
                                    break;
                                default:
                                    alignmentDescription = 'in the canvas';
                                    break;
                            }
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've centered the ${elementType} ${alignmentDescription}.`
                            };
                            
                            // Ensure changes are rendered
                            layer.relatedCanvasObject.canvas.renderAll();
                            
                        } catch (error) {
                            console.error(`Error in center_object:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error centering the object. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'change_layer_order') {
                        try {
                            // Find layer based on element_id or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected layer';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find a layer to change order. Please select an object first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);
                            
                            // Get current order from users_fields_order
                            let usersFieldsOrder = [];
                            if (this.selectedInstance.data.users_fields_order) {
                                try {
                                    usersFieldsOrder = JSON.parse(this.selectedInstance.data.users_fields_order);
                                } catch (e) {
                                    usersFieldsOrder = [];
                                }
                            }
                            
                            // If no order exists yet, get current order from canvas
                            if (!usersFieldsOrder.length) {
                                usersFieldsOrder = this.layersOrdered
                                    .filter(layer => layer.relatedCanvasObject)
                                    .map(layer => layer.id);
                            }
                            
                            // Find current layer's index
                            const currentIndex = usersFieldsOrder.indexOf(layer.id);
                            if (currentIndex === -1) return;
                            
                            // Create new order array based on requested position
                            let newOrder;
                            switch (functionArgs.position) {
                                case 'back':
                                    // Remove from current position and add to end (top)
                                    newOrder = [
                                        ...usersFieldsOrder.slice(0, currentIndex),
                                        ...usersFieldsOrder.slice(currentIndex + 1),
                                        layer.id
                                    ];
                                    break;
                                    
                                case 'front':
                                    // Remove from current position and add to start (bottom)
                                    newOrder = [
                                        layer.id,
                                        ...usersFieldsOrder.slice(0, currentIndex),
                                        ...usersFieldsOrder.slice(currentIndex + 1)
                                    ];
                                    break;
                                    
                                case 'backward':
                                    if (currentIndex < usersFieldsOrder.length - 1) {
                                        // Swap with previous item
                                        newOrder = [...usersFieldsOrder];
                                        [newOrder[currentIndex], newOrder[currentIndex + 1]] = 
                                        [newOrder[currentIndex + 1], newOrder[currentIndex]];
                                    } else {
                                        newOrder = usersFieldsOrder;
                                    }
                                    break;
                                    
                                case 'forward':
                                    if (currentIndex > 0) {
                                        // Swap with next item
                                        newOrder = [...usersFieldsOrder];
                                        [newOrder[currentIndex], newOrder[currentIndex - 1]] = 
                                        [newOrder[currentIndex - 1], newOrder[currentIndex]];
                                    } else {
                                        newOrder = usersFieldsOrder;
                                    }
                                    break;
                                    
                                default:
                                    throw new Error(`Invalid position: ${functionArgs.position}`);
                            }
                            
                            // Update the layersOrdered array to match the new order
                            this.layersOrdered = _.sortBy(this.layersOrdered, layer => 
                                newOrder.findIndex(id => id === layer.id)
                            );
                            
                            // Bring each layer to front in reverse order (like LayersModule)
                            [...this.layersOrdered]
                                .reverse()
                                .filter(layer => layer.relatedCanvasObject)
                                .forEach(layer => {
                                    (layer.relatedCanvasObject.group || layer.relatedCanvasObject).bringToFront();
                                });
                            
                            // Ensure ruler guide lines stay on top
                            this.moveRulerGuideLinesToFront();
                            
                            // Save the new order
                            this.selectedInstance.canvas.fire('layers:changed', {
                                instance_id: this.selectedInstance.id,
                                key: 'users_fields_order',
                                value: newOrder
                            });
                            
                            // Update the canvas
                            this.selectedInstance.canvas.renderAll();
                            
                            // Generate descriptive message
                            const positionDescriptions = {
                                'front': 'brought to the front',
                                'back': 'sent to the back',
                                'forward': 'brought forward',
                                'backward': 'sent backward'
                            };
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've ${positionDescriptions[functionArgs.position]} the layer with ID: "${functionArgs.element_id}".`
                            };
                        } catch (error) {
                            console.error(`Error in change_layer_order:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error changing the layer order. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'align_text') {
                        try {
                            // Common text element handling logic
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected text';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find a text element to modify. Please select a text element first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }
                            
                            // Check if this is actually a text element
                            const isText = layer.relatedCanvasObject.type === 'text' || 
                                layer.relatedCanvasObject.type === 'i-text' || 
                                layer.relatedCanvasObject.text !== undefined ||
                                (layer.relatedCanvasObject.id && 
                                 layer.relatedCanvasObject.id.includes('text_'));
                                 
                            if (!isText) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element is not a text element. Please select a text element.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Function-specific operations
                            if (functionName === 'align_text') {
                                // Map the user-friendly alignment values to the correct action
                                const alignmentMap = {
                                    'left': 'leftAlignToTemplate',
                                    'center': 'centerToTemplateWidth',
                                    'right': 'rightAlignToTemplate'
                                };

                                const alignFunction = alignmentMap[functionArgs.alignment] || 'leftAlignToTemplate';

                                // Fire the text alignment event with the function name directly
                                // This will be handled by the onTextEditorChange handler in show.js
                                editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                    action: alignFunction
                                });

                                // Apply to all templates if requested
                                if (functionArgs.apply_to_all) {
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        value: true,
                                        action: 'textAlign',
                                        keys: ['alignment']
                                    });
                                }

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've ${functionArgs.alignment}-aligned the text element with ID: "${functionArgs.element_id}" to the template.`
                                };
                            } 

                            // Common updates for all text operations
                            layer.relatedCanvasObject.canvas.renderAll();

                            // Fire object:moving for all operations to ensure changes are saved
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                        } catch (error) {
                            console.error(`Error in ${functionName}:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error modifying the text. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'edit_shape_color' || functionName === 'edit_shape_border') {
                        try {
                            // Common shape handling logic
                            let layer;
                            
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.id === functionArgs.element_id ||
                                            layer.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected shape';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find a shape to modify. Please select a shape element first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Improved shape detection logic
                            const isShape = layer.relatedCanvasObject.type && (
                                ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                                (layer.relatedCanvasObject.id && (
                                    layer.relatedCanvasObject.id.includes('circle') ||
                                    layer.relatedCanvasObject.id.includes('rect') ||
                                    layer.relatedCanvasObject.id.includes('shape') ||
                                    layer.relatedCanvasObject.id.includes('polygon') ||
                                    layer.relatedCanvasObject.id.includes('triangle')
                                ))
                            );

                            if (!isShape) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element with ID: "${functionArgs.element_id}" is not a shape.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            // Function-specific operations
                            if (functionName === 'edit_shape_color') {
                                editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                    action: 'bgColor',
                                    value: functionArgs.new_color,
                                    keys: functionArgs.apply_to_all ? ['fill_color'] : []
                                });

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've changed the fill color of the shape with ID: "${functionArgs.element_id}" to ${functionArgs.new_color}.`
                                };
                            } else if (functionName === 'edit_shape_border') {
                                if (functionArgs.border_color) {
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        action: 'strokeColor',
                                        value: functionArgs.border_color,
                                        keys: functionArgs.apply_to_all ? ['stroke_color'] : []
                                    });
                                }

                                if (functionArgs.border_width !== undefined) {
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        action: 'thickness',
                                        value: functionArgs.border_width,
                                        keys: functionArgs.apply_to_all ? ['thickness'] : []
                                    });
                                }

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've updated the border of the shape with ID: "${functionArgs.element_id}"${functionArgs.border_color ? ' with color: ' + functionArgs.border_color : ''}${functionArgs.border_width !== undefined ? ' and width: ' + functionArgs.border_width + 'px' : ''}.`
                                };
                            } 
                            // Common updates for all shape operations
                            layer.relatedCanvasObject.canvas.renderAll();
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                        } catch (error) {
                            console.error(`Error in ${functionName}:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error modifying the shape. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'resize_element') {
                        try {
                            // Find layer based on element_id or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                         layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an element to resize. Please select an element first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            const obj = layer.relatedCanvasObject;
                            
                            // Check if this is a valid resizable object (image or shape)
                            const isImage = obj.type === 'image' || (obj.id && obj.id.includes('image_'));
                            const isShape = obj.type === 'rect' || obj.type === 'circle' || obj.type === 'CustomRect' ||
                                (obj.id && (
                                    obj.id.includes('circle') ||
                                    obj.id.includes('rect') ||
                                    obj.id.includes('shape')
                                ));
                                
                            if (!isImage && !isShape) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element cannot be resized. Only images and shapes can be resized with this function.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(obj);
                            
                            // Get original dimensions
                            const originalWidth = obj.width * obj.scaleX;
                            const originalHeight = obj.height * obj.scaleY;
                            
                            // Determine scale factor to use
                            let scaleFactor;
                            
                            if (functionArgs.scale !== undefined) {
                                // Use provided scale factor
                                scaleFactor = functionArgs.scale;
                            } else if (functionArgs.scale_direction) {
                                // Use scale direction with default 10% change
                                const defaultScaleChange = 0.1; // 10%
                                if (functionArgs.scale_direction === 'up') {
                                    scaleFactor = 1 + defaultScaleChange; // 110%
                                } else { // 'down'
                                    scaleFactor = 1 - defaultScaleChange; // 90%
                                }
                            } else {
                                // No scale specified, use default increase of 10%
                                scaleFactor = 1.1;
                            }
                            
                            // Calculate new dimensions
                            const newWidth = originalWidth * scaleFactor;
                            const newHeight = originalHeight * scaleFactor;
                            
                            // Handle different object types
                            const isCircle = obj.type === 'circle' || 
                                (obj.id && obj.id.includes('circle')) || 
                                (obj.element_id && obj.element_id.includes('circle'));
                                
                            const isCustomRect = obj.type === 'CustomRect' || 
                                (obj.id && obj.id.includes('rect'));
                                
                            if (isCircle) {
                                // For circle objects, adjust the radius instead of width/height
                                const currentRadius = obj.radius || (obj.width / 2);
                                const newRadius = currentRadius * scaleFactor;
                                
                                obj.set({
                                    radius: newRadius,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                            } else if (isCustomRect || (obj.scaleX === 1 && obj.scaleY === 1)) {
                                // Handle CustomRect or simple resize for non-scaled objects
                                obj.set({
                                    width: newWidth,
                                    height: newHeight,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                            } else {
                                // Set scale values based on the desired dimensions
                                const newScaleX = newWidth / obj.width;
                                const newScaleY = newHeight / obj.height;
                                
                                obj.set({
                                    scaleX: newScaleX,
                                    scaleY: newScaleY
                                });
                            }
                            
                            // Update coordinates
                            obj.setCoords();
                            
                            // Validate the resizing operation
                            if (!isCircle && (obj.width <= 0 || obj.height <= 0)) {
                                console.warn('Resize resulted in invalid dimensions, reverting to original size');
                                obj.set({
                                    width: originalWidth,
                                    height: originalHeight,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                                obj.setCoords();
                            } else if (isCircle && obj.radius <= 0) {
                                console.warn('Resize resulted in invalid radius, reverting to original size');
                                obj.set({
                                    radius: originalWidth / 2,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                                obj.setCoords();
                            }
                            
                            // Apply to all templates if requested
                            if (functionArgs.apply_to_all) {
                                if (isImage) {
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        value: true,
                                        action: 'scale',
                                        keys: ['scale']
                                    });
                                } else if (isShape) {
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        value: true,
                                        action: 'width',
                                        keys: ['width']
                                    });
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        value: true,
                                        action: 'height',
                                        keys: ['height']
                                    });
                                }
                            }
                            
                            // Update the canvas
                            obj.canvas.renderAll();
                            
                            // Fire appropriate events
                            obj.canvas.fire('object:modified', { target: obj });
                            obj.canvas.fire('object:moving', { target: obj });
                            
                            // Create appropriate response message
                            let resizeMethod = '';
                            if (functionArgs.scale !== undefined) {
                                resizeMethod = `scaled to ${Math.round(functionArgs.scale * 100)}%`;
                            } else if (functionArgs.scale_direction) {
                                if (functionArgs.scale_direction === 'up') {
                                    resizeMethod = 'increased by 10%';
                                } else {
                                    resizeMethod = 'decreased by 10%';
                                }
                            } else {
                                resizeMethod = 'increased by 10%';
                            }
                            
                            // Determine element type for response message
                            let elementType;
                            if (isCircle) {
                                elementType = 'circle';
                            } else if (isImage) {
                                elementType = 'image';
                            } else if (obj.type === 'rect' || obj.type === 'CustomRect' || 
                                (obj.id && obj.id.includes('rect'))) {
                                elementType = 'rectangle';
                            } else {
                                elementType = 'shape';
                            }
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've ${resizeMethod} the ${elementType} with ID: "${functionArgs.element_id}".`
                            };
                            
                        } catch (error) {
                            console.error(`Error in resize_element:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error resizing the element. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'move_object') {
                        try {
                            // Find the target element by ID or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                                    this.selectedInstance.canvas.getActiveObject() : null;

                                const selectedElement = activeObject || this.targetElement || this.target;

                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer =>
                                        layer.relatedCanvasObject === selectedElement ||
                                        (layer.relatedCanvasObject &&
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                                layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );

                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected object';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an object to move. Please select an object first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);

                            // Determine the type of object we're working with
                            const isImage = layer.relatedCanvasObject.type === 'image' ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('image'));

                            const isText = layer.relatedCanvasObject.type === 'text' ||
                                layer.relatedCanvasObject.type === 'i-text' ||
                                layer.relatedCanvasObject.text !== undefined ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('text_'));

                            const isShape = layer.relatedCanvasObject.type && (
                                ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                                (layer.relatedCanvasObject.id && (
                                    layer.relatedCanvasObject.id.includes('circle') ||
                                    layer.relatedCanvasObject.id.includes('rect') ||
                                    layer.relatedCanvasObject.id.includes('shape') ||
                                    layer.relatedCanvasObject.id.includes('polygon') ||
                                    layer.relatedCanvasObject.id.includes('triangle')
                                ))
                            );

                            // Common movement calculations
                            const direction = functionArgs.direction;
                            const distance = functionArgs.distance || 10; // Default to 10px if not specified

                            let x = 0;
                            let y = 0;

                            // Set movement coordinates based on direction
                            switch (direction) {
                                case 'up': y = -distance; break;
                                case 'down': y = distance; break;
                                case 'left': x = -distance; break;
                                case 'right': x = distance; break;
                            }

                            // Get current position
                            const currentPosition = layer.relatedCanvasObject.getPointByOrigin('left', 'top');
                            
                            // Special handling for product images (image_X)
                            const isProductImage = isImage && layer.relatedCanvasObject.id && 
                                (layer.relatedCanvasObject.id.includes('image_') || 
                                 layer.relatedCanvasObject.id.includes('product_image'));
                                
                            // Set new position
                            layer.relatedCanvasObject.setPositionByOrigin(
                                { x: currentPosition.x + x, y: currentPosition.y + y },
                                'left',
                                'top'
                            );

                            // Update coordinates
                            layer.relatedCanvasObject.setCoords();

                            // Apply to all templates if requested (only for text and shape)
                            if ((isText || isShape) && functionArgs.apply_to_all) {
                                const eventName = isText ? EVENTS.TEXT_CHANGED : EVENTS.SHAPE_CHANGED;
                                const xKey = isText ? 'x' : 'offset_x';
                                const yKey = isText ? 'y' : 'offset_y';

                                if (x !== 0) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'x',
                                        keys: [xKey]
                                    });
                                }

                                if (y !== 0) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'y',
                                        keys: [yKey]
                                    });
                                }
                            }

                            // Common updates for all object movements
                            layer.relatedCanvasObject.canvas.renderAll();
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                            // Generate appropriate message based on object type
                            const objectType = isImage ? 'image' : (isText ? 'text' : (isShape ? 'shape' : 'object'));
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've moved the ${objectType} ${direction} by ${distance}px. This was a relative movement, not setting absolute coordinates.`
                            };

                        } catch (error) {
                            console.error(`Error in move_object:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error moving the object. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === 'position_object') {
                        try {
                            // Find the target element by ID or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                                    this.selectedInstance.canvas.getActiveObject() : null;

                                const selectedElement = activeObject || this.targetElement || this.target;

                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer =>
                                        layer.relatedCanvasObject === selectedElement ||
                                        (layer.relatedCanvasObject &&
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                                layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );

                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected object';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an object to position. Please select an object first.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);

                            // Determine the type of object we're working with
                            const isImage = layer.relatedCanvasObject.type === 'image' ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('image'));

                            const isText = layer.relatedCanvasObject.type === 'text' ||
                                layer.relatedCanvasObject.type === 'i-text' ||
                                layer.relatedCanvasObject.text !== undefined ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('text_'));

                            const isShape = layer.relatedCanvasObject.type && (
                                ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                                (layer.relatedCanvasObject.id && (
                                    layer.relatedCanvasObject.id.includes('circle') ||
                                    layer.relatedCanvasObject.id.includes('rect') ||
                                    layer.relatedCanvasObject.id.includes('shape') ||
                                    layer.relatedCanvasObject.id.includes('polygon') ||
                                    layer.relatedCanvasObject.id.includes('triangle')
                                ))
                            );

                            // Get X and Y coordinates or use existing ones
                            const newX = typeof functionArgs.x === 'number' ? functionArgs.x : null;
                            const newY = typeof functionArgs.y === 'number' ? functionArgs.y : null;
                            
                            // Check if at least one coordinate is provided
                            if (newX === null && newY === null) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I need either an X coordinate, Y coordinate, or both to position the object. Please specify at least one.`
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                                return;
                            }
                            
                            // Get current position
                            const currentPosition = layer.relatedCanvasObject.getPointByOrigin('left', 'top');
                            
                            // Store old position for reporting change
                            const oldX = Math.round(currentPosition.x);
                            const oldY = Math.round(currentPosition.y);
                            
                            // Special handling for product images (image_X)
                            const isProductImage = isImage && layer.relatedCanvasObject.id && 
                                (layer.relatedCanvasObject.id.includes('image_') || 
                                 layer.relatedCanvasObject.id.includes('product_image'));
                                
                            // Set new position
                            layer.relatedCanvasObject.setPositionByOrigin(
                                { 
                                    x: newX !== null ? newX : currentPosition.x, 
                                    y: newY !== null ? newY : currentPosition.y 
                                },
                                'left',
                                'top'
                            );

                            // Update coordinates
                            layer.relatedCanvasObject.setCoords();

                            // Apply to all templates if requested (only for text and shape)
                            if ((isText || isShape) && functionArgs.apply_to_all) {
                                const eventName = isText ? EVENTS.TEXT_CHANGED : EVENTS.SHAPE_CHANGED;
                                const xKey = isText ? 'x' : 'offset_x';
                                const yKey = isText ? 'y' : 'offset_y';

                                if (newX !== null) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'x',
                                        keys: [xKey]
                                    });
                                }

                                if (newY !== null) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'y',
                                        keys: [yKey]
                                    });
                                }
                            }

                            // Common updates for all object positioning
                            layer.relatedCanvasObject.canvas.renderAll();
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                            // Generate appropriate message based on object type and what was changed
                            const objectType = isImage ? 'image' : (isText ? 'text' : (isShape ? 'shape' : 'object'));
                            
                            let positionMessage = '';
                            const elementName = layer.element_id || layer.id || 'selected element';
                            
                            if (newX !== null && newY !== null) {
                                positionMessage = `I've set the exact coordinates of the ${objectType} to X: ${newX}px, Y: ${newY}px (changed from X: ${oldX}px, Y: ${oldY}px).`;
                            } else if (newX !== null) {
                                positionMessage = `I've set the exact X coordinate of the ${objectType} to ${newX}px (changed from ${oldX}px). The Y coordinate remains at ${oldY}px.`;
                            } else if (newY !== null) {
                                positionMessage = `I've set the exact Y coordinate of the ${objectType} to ${newY}px (changed from ${oldY}px). The X coordinate remains at ${oldX}px.`;
                            }
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: positionMessage
                            };

                        } catch (error) {
                            console.error(`Error in position_object:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error positioning the object. Please try again.`
                            };
                        } finally {
                            this.loading = false;
                        }
                    } else if (functionName === "resize_banner") {
                        const instanceId = this.selectedInstance.id;
                        const grid = $("#template-group-preview");
                        const customer_id = grid.attr("data-customer-id");
                        const layout_id = grid.attr("data-layout-id");
                        try {
                            this.loading = true;
                            const { width, height } = functionArgs;
                            const canvasElement = this.selectedInstance.canvas.getElement();
                            canvasElement.toBlob(async (blob) => {
                                const formData = new FormData();
                                formData.append("width", width);
                                formData.append("height", height);
                                formData.append("thumbnail", blob, "thumbnail.png");
                                this.loading = true;
                                const response = await axios.post(`/banner/${customer_id}/group/${layout_id}/template/${instanceId}/ai_resize`, formData, {
                                    headers: {
                                        "Content-Type": "multipart/form-data",
                                    },
                                });
                                console.log(response);
                                assistantMessage = {
                                    role: "assistant",
                                    content: `Here is how the banner can look like with ${width}px width and ${height}px height.`,
                                    imageUrl: response.data.image
                                };
                                this.messages.push(assistantMessage);
                                this.loading = false;
                            }, "image/png");
                        } catch (error) {
                            console.error(`Error in resize_banner:`, error);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error resizing the banner. Please try again.`,
                            };
                        } finally {
                            this.loading = false;
                        }
                    }
                } else {
                    assistantMessage = {
                        role: "assistant",
                        content: responseMessage.content,
                    };
                }

                if (assistantMessage) {
                    this.messages.push(assistantMessage);
                    this.loading = false;
                }
            } catch (error) {
                console.error("Error:", error);
                this.messages.push({
                    role: "assistant",
                    content:
                        "Sorry, there was an error processing your message.",
                });
                this.loading = false;
            } finally {
                this.$nextTick(() => {
                    const messagesDiv = this.$refs.messages;
                    messagesDiv.scrollTop = messagesDiv.scrollHeight;
                });
            }
        },
        // Helper method to determine the event type based on the element type
        getElementEventType(object) {
            // Determine the appropriate event type based on object properties
            if (object.type === 'text' || object.type === 'i-text' || object.text !== undefined) {
                return EVENTS.TEXT_CHANGED;
            } else if (object.type === 'image' || object.id && object.id.includes('image')) {
                return EVENTS.IMAGE_CHANGED;
            } else if (['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(object.type) ||
                (object.id && (
                    object.id.includes('circle') ||
                    object.id.includes('rect') ||
                    object.id.includes('shape') ||
                    object.id.includes('polygon') ||
                    object.id.includes('triangle')
                ))) {
                return EVENTS.SHAPE_CHANGED;
            }
            
            // Default to shape changed if type can't be determined
            return EVENTS.SHAPE_CHANGED;
        },
        // Helper method to determine the element type
        getElementType(object) {
            // Check what type of object this is based on properties
            if (!object) return 'object';
            
            if (object.type === 'textbox' || object.type === 'i-text' || object.id && object.id.startsWith('text_')) {
                return 'text';
            } else if (object.type === 'image' || object.id && object.id.startsWith('image_')) {
                return 'image';
            } else if (object.type === 'rect' || object.type === 'circle' || object.id && (object.id.startsWith('rect_') || object.id.startsWith('circle_') || object.id.startsWith('additional_field_rect_'))) {
                return 'shape';
            }
            
            return 'object';
        },
        selectProduct(productId) {
            $("#addProductImages").modal("show");

            setTimeout(() => {
                const searchInput = $(
                    "#addProductImages .c-top-bar__search--input",
                );
                if (searchInput.length) {
                    searchInput.val(productId);
                    searchInput.trigger("input");

                    const events = ["keydown", "keypress", "keyup"];
                    events.forEach((eventType) => {
                        const event = new Event(eventType, { bubbles: true });
                        Object.defineProperties(event, {
                            keyCode: { value: 13 },
                            key: { value: "Enter" },
                            which: { value: 13 },
                            code: { value: "Enter" },
                        });
                        searchInput[0].dispatchEvent(event);
                    });
                }
            }, 300);
        },
        copyProofLink(id) {
            const input = document.getElementById(id);
            input.select();
            document.execCommand("copy");
            alert("Proof link copied to clipboard!");
        },
    },
};
</script>

<style scoped lang="scss">
.chat-module {
    height: calc(100vh - 98px);
}
.chat-module .card {
    position: relative;
    overflow-y: auto;
}
.message {
    margin: 10px;
    padding: 10px;
    border-radius: 10px;
}
.message p {
    white-space: pre-wrap;
    margin-bottom: 0px;
}
.message-user {
    background-color: #209c86;
    color: white;
    text-align: right;
}
.message-assistant {
    background-color: #e9ecef;
    text-align: left;
}
</style>
